import _ from 'lodash';

import {
  ProductionRecordDetailFieldEnum as ProdRecordDetailFields,
  ProductionRecordFieldEnum as ProdRecordFields,
  CloseProductionFieldEnum as CloseProdFields,
} from 'Common/FieldEnum';
import { createMap } from 'Common/Helpers/format';

import * as ProdRecordHelpers from './productionRecord';
import { ProductionPlanFields } from './Constants';

export const getViewProdRecordIndex = (state) => state.viewProdRecordIndex || 0;

export const getProdRecords = (state) => state.prodRecords;

export const getDetailsByRecipeCategory = (state) =>
  state.detailsByRecipeCategory;

export const getProdRecord = (state) => {
  const prodRecords = getProdRecords(state);
  const viewProdRecordIndex = getViewProdRecordIndex(state);

  return prodRecords[viewProdRecordIndex] || {};
};

export const getFeedingFigure = (state) => {
  const prodRecord = getProdRecord(state);
  return _.get(prodRecord, ProdRecordFields.FeedingFigure, 0);
};

export const getFeedingFigurePreK = (state) => {
  const prodRecord = getProdRecord(state);
  return _.get(prodRecord, ProdRecordFields.FeedingFigurePreK, 0);
};

export const getServedPreK = (state) => {
  const prodRecord = getProdRecord(state);
  const ret = _.get(prodRecord, ProdRecordFields.ServedPreK, 0);
  return ret;
};

export const getServingLineNumbers = (state) => {
  const prodRecord = getProdRecord(state);
  const ret = _.get(prodRecord, ProdRecordDetailFields.ServingLineNumbers);
  return ret;
};

export const getFeedingFigureAlacarte = (state) => {
  const prodRecord = getProdRecord(state);
  return _.get(prodRecord, ProdRecordFields.FeedingFigureAlacarte, 0);
};

export const getFeedingFigureAdult = (state) => {
  const prodRecord = getProdRecord(state);
  return _.get(prodRecord, ProdRecordFields.FeedingFigureAdult, 0);
};

export const getProdDetails = (state) => {
  const prodRecord = getProdRecord(state);

  return _.get(prodRecord, ProdRecordFields.Details, []);
};

export const getProdDetail = (state, index) => {
  const prodDetails = getProdDetails(state);

  return prodDetails[index];
};

export const getRecipeCategoryFieldTotal = (state, field, recipeCategoryId) => {
  const detailsByRecipeCategory = getDetailsByRecipeCategory(state);

  if (!detailsByRecipeCategory[recipeCategoryId]) {
    return 0;
  }

  return ProdRecordHelpers.computeColumnTotal(
    detailsByRecipeCategory[recipeCategoryId],
    field
  );
};

export const getRecipeCategoryPlannedPercentageMessage = (
  state,
  recipeCategoryId
) => {
  const feedingFigure = getFeedingFigure(state);
  const percentage = getRecipeCategoryFieldTotal(
    state,
    ProdRecordDetailFields.PlannedPercentage,
    recipeCategoryId
  );

  let extraOrMissingAmount = 0;
  let result = '';

  if (
    ProdRecordHelpers.isEntree(recipeCategoryId) ||
    ProdRecordHelpers.isMilk(recipeCategoryId)
  ) {
    if (feedingFigure > 0) {
      const categoryTotal = getRecipeCategoryFieldTotal(
        state,
        ProdRecordDetailFields.ReimbursableMeals,
        recipeCategoryId
      );

      extraOrMissingAmount = feedingFigure - categoryTotal;

      if (extraOrMissingAmount) {
        result = `${percentage.toFixed(2)}% / ${
          extraOrMissingAmount > 0 ? '' : '+'
        }${-extraOrMissingAmount}`;
      } else {
        result = '100.00%';
      }
    }
  } else {
    result = `${percentage.toFixed(2)}%`;
  }

  return result;
};

export const getRecipeCategoryPlannedTotal = (state, recipeCategoryId) => {
  const detailsByRecipeCategory = getDetailsByRecipeCategory(state);
  const prodRecord = getProdRecord(state);

  if (!detailsByRecipeCategory[recipeCategoryId] || !prodRecord) {
    return 0;
  }

  return ProdRecordHelpers.isEntree(recipeCategoryId)
    ? ProdRecordHelpers.computeFeedingFigureTotal(prodRecord)
    : _.sumBy(detailsByRecipeCategory[recipeCategoryId], (d) => {
        if (ProdRecordHelpers.isDetailIngredient(d)) {
          return 0;
        }
        return ProdRecordHelpers.computePlannedTotal(d);
      });
};

export const getProdRecordRecipesById = (state) => {
  const details = getProdDetails(state);
  const detailRecipes = details.filter(
    (detail) => !ProdRecordHelpers.isDetailIngredient(detail)
  );

  return createMap(detailRecipes, ProdRecordDetailFields.RecipeId);
};

export const getRecipeCategoryServedReimbursableTotal = (
  state,
  recipeCategoryId
) => {
  const detailsByRecipeCategory = getDetailsByRecipeCategory(state);

  if (!detailsByRecipeCategory[recipeCategoryId]) {
    return 0;
  }

  return _.sumBy(detailsByRecipeCategory[recipeCategoryId], (d) => {
    if (ProdRecordHelpers.isDetailIngredient(d)) {
      return 0;
    }
    return ProdRecordHelpers.computeReimbServedTotal(d);
  });
};

export const getProductionPlanInitialValues = (state) => {
  const prodRecord = getProdRecord(state);
  const initialValues = _.pick(prodRecord, ProductionPlanFields);
  initialValues[CloseProdFields.SignAndVerify] = '';

  return initialValues;
};

export const getFieldTotalByRecCat = (state, field, recipeCategoryIds) => {
  const reimbTotalsByRecCat = {};
  recipeCategoryIds.forEach((rcId) => {
    reimbTotalsByRecCat[rcId] = getRecipeCategoryFieldTotal(state, field, rcId);
  });

  return reimbTotalsByRecCat;
};

export const getPlannedPercMessagesByRecCat = (state, recipeCategoryIds) => {
  const percMessagesByRecCat = {};
  recipeCategoryIds.forEach((rcId) => {
    percMessagesByRecCat[rcId] = getRecipeCategoryPlannedPercentageMessage(
      state,
      rcId
    );
  });

  return percMessagesByRecCat;
};

export const getPlannedTotalByRecCat = (state, recipeCategoryIds) => {
  const plannedTotalsByRecCat = {};
  recipeCategoryIds.forEach((rcId) => {
    plannedTotalsByRecCat[rcId] = getRecipeCategoryPlannedTotal(state, rcId);
  });

  return plannedTotalsByRecCat;
};

export const getServedReimbTotalByRecCat = (state, recipeCategoryIds) => {
  const servedReimbTotalsByRecCat = {};

  recipeCategoryIds.forEach((rcId) => {
    servedReimbTotalsByRecCat[rcId] = getRecipeCategoryServedReimbursableTotal(
      state,
      rcId
    );
  });

  return servedReimbTotalsByRecCat;
};



// WEBPACK FOOTER //
// ./src/pages/manageproduction/helpers/formSelectors.js